import React, { useState, useEffect, useRef } from "react";
import { Printer, Save, Building2, AlertTriangle, RotateCcw, Loader2, Upload, Image as ImageIcon, X, Download, FileUp, Database, ShieldCheck, ChevronRight, Truck, ShoppingBasket, Carrot, FileText, Tag, Mail, Users, Package, FileSpreadsheet, Wrench, Bell, BellRing } from "lucide-react";
import { downloadBlob } from "../lib/download";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { LABEL_FORMATS, getSettings, loadSettings, saveSettings } from "../lib/printer";
import { api } from "../lib/api";
import { pdf } from "@react-pdf/renderer";
import FieldOpsGuidePdf from "../components/FieldOpsGuidePdf";
import ApiKeysManager from "../components/ApiKeysManager";
import ResetProductionDialog from "../components/ResetProductionDialog";
import FixMissingDlcDialog from "../components/FixMissingDlcDialog";
import FixOrphanLotsDialog from "../components/FixOrphanLotsDialog";
import TranslateProductsDialog from "../components/TranslateProductsDialog";
import ScavCantonSection from "../components/settings/ScavCantonSection";
import CronWasteAuditPanel from "../components/CronWasteAuditPanel";
import AdjustAuditPanel from "../components/AdjustAuditPanel";
import PhantomNayaxSalesPanel from "../components/PhantomNayaxSalesPanel";
import SalesReviewQueuePanel from "../components/SalesReviewQueuePanel";
import SettingsQuickNav from "../components/SettingsQuickNav";
import { toast } from "sonner";

export default function Settings() {
  const [form, setForm] = useState(getSettings());
  const [distributors, setDistributors] = useState([]);
  const [resetting, setResetting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState(null); // { counts, raw }
  const [importMode, setImportMode] = useState("merge");
  const [downloadingGuide, setDownloadingGuide] = useState(false);

  const downloadFieldOpsGuide = async () => {
    setDownloadingGuide(true);
    try {
      const logoUrl = `${window.location.origin}/logo-maisonthai-pdf.png`;
      const blob = await pdf(<FieldOpsGuidePdf logoUrl={logoUrl} />).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Guide-tournee-MaisonThai-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Guide téléchargé — imprimable A4");
    } catch (e) {
      toast.error(`Erreur génération guide : ${e?.message || e}`);
    } finally {
      setDownloadingGuide(false);
    }
  };
  const fileInputRef = useRef(null);
  const signatureInputRef = useRef(null);
  const backupInputRef = useRef(null);

  useEffect(() => {
    loadSettings({ withLogo: true }).then((s) => setForm(s)).catch(() => {});
    api.get("/distributors").then(({ data }) => setDistributors(data)).catch(() => {});
  }, []);

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Veuillez sélectionner une image (PNG, JPG, SVG…)");
      return;
    }
    if (file.size > 1024 * 1024) {
      toast.error("Image trop volumineuse (max 1 Mo)");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setForm((f) => ({ ...f, company_logo: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const removeLogo = () => setForm((f) => ({ ...f, company_logo: "" }));

  // Phase 101dd — Upload signature scannée (PNG transparent recommandé)
  const handleSignatureUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Veuillez sélectionner une image (PNG transparent recommandé)");
      return;
    }
    if (file.size > 1024 * 1024) {
      toast.error("Image trop volumineuse (max 1 Mo)");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setForm((f) => ({ ...f, company_signature_image: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const removeSignature = () => setForm((f) => ({ ...f, company_signature_image: "" }));

  const save = async () => {
    setSaving(true);
    try {
      await saveSettings(form);
      toast.success("Paramètres enregistrés (synchronisés sur tous les postes)");
    } catch {
      toast.error("Impossible d'enregistrer côté serveur");
    } finally {
      setSaving(false);
    }
  };

  const resetOperations = async () => {
    setResetting(true);
    try {
      const { data } = await api.post("/admin/reset-operations");
      toast.success(
        `Opérations remises à zéro : ${data.lots_deleted} lots, ${data.movements_deleted} mouvements, ${data.orders_deleted} OF, ${data.inventories_deleted} inventaires supprimés.`
      );
    } catch (err) {
      toast.error("Échec de la remise à zéro");
    } finally {
      setResetting(false);
    }
  };

  // Phase 87au — Backfill production_date sur BLs et LabTests historiques
  const [backfillingDates, setBackfillingDates] = useState(false);
  const backfillProductionDates = async () => {
    setBackfillingDates(true);
    try {
      const { data } = await api.post("/admin/backfill-production-date");
      toast.success(
        `Rétro-backfill terminé : ${data.bl_lines_updated} lignes BL et ${data.lab_tests_updated} test(s) labo enrichis avec la date de fabrication.`,
        { duration: 8000 }
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec du backfill");
    } finally {
      setBackfillingDates(false);
    }
  };

  // Phase 87am — Nettoyage des lots fantômes sur shop-online-virtual
  const [cleaningGhosts, setCleaningGhosts] = useState(false);
  const cleanupGhostLots = async () => {
    setCleaningGhosts(true);
    try {
      const { data } = await api.post("/admin/cleanup-shop-online-virtual-lots");
      if (data.cleaned_count === 0) {
        toast.success("Aucun lot fantôme à nettoyer — votre stock est déjà propre 👌");
      } else {
        toast.success(`${data.cleaned_count} lot(s) fantôme(s) régularisé(s). Rechargez la page Stocks pour voir le résultat.`, { duration: 8000 });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec du nettoyage");
    } finally {
      setCleaningGhosts(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/admin/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      downloadBlob(blob, `agromanage-backup-${stamp}.json`);
      const total = Object.values(data.counts).reduce((s, n) => s + n, 0);
      toast.success(`Sauvegarde générée (${total} enregistrements)`);
    } catch {
      toast.error("Échec de l'export");
    } finally {
      setExporting(false);
    }
  };

  const handleBackupSelected = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow reselect same file
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target.result);
        if (!parsed.collections || typeof parsed.collections !== "object") {
          throw new Error("Format invalide");
        }
        const counts = {};
        Object.entries(parsed.collections).forEach(([k, v]) => {
          counts[k] = Array.isArray(v) ? v.length : 0;
        });
        setImportPreview({ counts, raw: parsed, exported_at: parsed.exported_at });
      } catch {
        toast.error("Fichier de sauvegarde invalide");
      }
    };
    reader.readAsText(file);
  };

  const confirmImport = async () => {
    if (!importPreview) return;
    setImporting(true);
    try {
      const { data } = await api.post("/admin/import", {
        collections: importPreview.raw.collections,
        mode: importMode,
      });
      const totalInserted = Object.values(data.summary).reduce((s, x) => s + x.inserted, 0);
      const totalUpdated = Object.values(data.summary).reduce((s, x) => s + x.updated, 0);
      toast.success(`Import réussi : ${totalInserted} ajoutés, ${totalUpdated} mis à jour (mode ${importMode})`);
      setImportPreview(null);
    } catch {
      toast.error("Échec de l'import");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="settings-page">
      <SettingsQuickNav />
      <div className="bg-white border border-[#E5E5E0] rounded-lg p-6">
        <div className="flex items-center gap-2 mb-5">
          <Printer className="w-5 h-5 text-[#1F4E3D]" />
          <h2 className="font-heading text-xl font-medium">Imprimante étiquettes</h2>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="mb-1.5 block">Nom de l'imprimante</Label>
              <Input
                value={form.printer_name}
                onChange={(e) => setForm({ ...form, printer_name: e.target.value })}
                placeholder="Epson ColorWorks C3500"
                data-testid="printer-name"
              />
              <p className="text-xs text-[#575A57] mt-1.5">Utilisé comme nom par défaut dans le dialogue d'impression.</p>
            </div>
            <div>
              <Label className="mb-1.5 block">Adresse IP (optionnel)</Label>
              <Input
                value={form.printer_ip}
                onChange={(e) => setForm({ ...form, printer_ip: e.target.value })}
                placeholder="192.168.1.50"
                data-testid="printer-ip"
              />
              <p className="text-xs text-[#575A57] mt-1.5">Informatif — l'impression passe par le pilote Epson local.</p>
            </div>
          </div>
          <div>
            <Label className="mb-1.5 block">Format d'étiquette par défaut</Label>
            <Select value={form.default_format} onValueChange={(v) => setForm({ ...form, default_format: v })}>
              <SelectTrigger data-testid="default-format"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.values(LABEL_FORMATS).map((f) => (
                  <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-[#575A57] mt-1.5">Compatible avec les rouleaux standards Epson ColorWorks C3500.</p>
          </div>

          <div className="bg-[#F9F9F6] border border-[#E5E5E0] rounded p-4 text-sm text-[#575A57]">
            <div className="font-medium text-[#1A1C1A] mb-1.5 flex items-center gap-2">
              <Printer className="w-4 h-4" /> Conseils pour l'Epson ColorWorks C3500
            </div>
            <ul className="space-y-1 text-xs list-disc pl-5">
              <li>Installez le pilote Epson C3500 sur votre poste avant la première impression.</li>
              <li>Dans le dialogue d'impression du navigateur, sélectionnez l'imprimante <span className="font-mono">{form.printer_name}</span>.</li>
              <li>Configurez le format papier dans les paramètres avancés du pilote pour correspondre au rouleau chargé.</li>
              <li>Désactivez les marges automatiques du navigateur pour un rendu optimal sur étiquettes continues.</li>
            </ul>
          </div>
        </div>
      </div>

      <Link
        to="/parametres/utilisateurs"
        className="block bg-white border border-[#E5E5E0] rounded-lg p-6 hover:border-[#1F4E3D]/40 hover:shadow-sm transition-all group"
        data-testid="link-users"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-[#E8EEE9] flex items-center justify-center shrink-0">
              <ShieldCheck className="w-5 h-5 text-[#1F4E3D]" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-medium text-[#1A1C1A]">Comptes utilisateurs</h2>
              <p className="text-sm text-[#575A57] mt-0.5">
                Créez et gérez les accès admin et opérateurs.
              </p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-[#A3A6A3] group-hover:text-[#1F4E3D] group-hover:translate-x-0.5 transition-all" />
        </div>
      </Link>

      <Link
        to="/parametres/fournisseurs"
        className="block bg-white border border-[#E5E5E0] rounded-lg p-6 hover:border-[#1F4E3D]/40 hover:shadow-sm transition-all group"
        data-testid="link-suppliers"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-[#FBF4E9] flex items-center justify-center shrink-0">
              <Truck className="w-5 h-5 text-[#8C6B2F]" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-medium text-[#1A1C1A]">Fournisseurs</h2>
              <p className="text-sm text-[#575A57] mt-0.5">
                Carnet d'adresses (Aligro, Manor, marchés…) — sélectionnable depuis chaque ingrédient.
              </p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-[#A3A6A3] group-hover:text-[#1F4E3D] group-hover:translate-x-0.5 transition-all" />
        </div>
      </Link>

      <Link
        to="/parametres/ingredients"
        className="block bg-white border border-[#E5E5E0] rounded-lg p-6 hover:border-[#1F4E3D]/40 hover:shadow-sm transition-all group"
        data-testid="link-ingredients"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-[#EAF4EE] flex items-center justify-center shrink-0">
              <Carrot className="w-5 h-5 text-[#1F4E3D]" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-medium text-[#1A1C1A]">Ingrédients de base</h2>
              <p className="text-sm text-[#575A57] mt-0.5">
                Référentiel partagé des ingrédients utilisés dans les fiches techniques (prix, fournisseur préféré, allergènes).
              </p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-[#A3A6A3] group-hover:text-[#1F4E3D] group-hover:translate-x-0.5 transition-all" />
        </div>
      </Link>

      <Link
        to="/parametres/liste-courses"
        className="block bg-white border border-[#E5E5E0] rounded-lg p-6 hover:border-[#1F4E3D]/40 hover:shadow-sm transition-all group"
        data-testid="link-shopping-list"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-[#EEF2F7] flex items-center justify-center shrink-0">
              <ShoppingBasket className="w-5 h-5 text-[#3B5B7A]" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-medium text-[#1A1C1A]">Liste de courses</h2>
              <p className="text-sm text-[#575A57] mt-0.5">
                Cumule les ingrédients de plusieurs productions et regroupe par fournisseur. Export PDF.
              </p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-[#A3A6A3] group-hover:text-[#1F4E3D] group-hover:translate-x-0.5 transition-all" />
        </div>
      </Link>

      <Link
        to="/parametres/pages-legales"
        className="block bg-white border border-[#E5E5E0] rounded-lg p-6 hover:border-[#1F4E3D]/40 hover:shadow-sm transition-all group"
        data-testid="link-legal-pages"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-[#FBF4E9] flex items-center justify-center shrink-0">
              <FileText className="w-5 h-5 text-[#8C6B2F]" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-medium text-[#1A1C1A]">Pages éditoriales boutique</h2>
              <p className="text-sm text-[#575A57] mt-0.5">
                Éditez Histoire, Mentions légales, CGV et Politique de confidentialité (Markdown).
              </p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-[#A3A6A3] group-hover:text-[#1F4E3D] group-hover:translate-x-0.5 transition-all" />
        </div>
      </Link>

      <Link
        to="/parametres/categories"
        className="block bg-white border border-[#E5E5E0] rounded-lg p-6 hover:border-[#1F4E3D]/40 hover:shadow-sm transition-all group"
        data-testid="link-categories"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-[#EAF3EE] flex items-center justify-center shrink-0">
              <Tag className="w-5 h-5 text-[#1F4E3D]" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-medium text-[#1A1C1A]">Catégories produits</h2>
              <p className="text-sm text-[#575A57] mt-0.5">
                Créez, renommez et réorganisez les catégories affichées sur la boutique (Poulet, Bœuf, Pad Thaï…).
              </p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-[#A3A6A3] group-hover:text-[#1F4E3D] group-hover:translate-x-0.5 transition-all" />
        </div>
      </Link>

      <Link
        to="/parametres/livraison"
        className="block bg-white border border-[#E5E5E0] rounded-lg p-6 hover:border-[#1F4E3D]/40 hover:shadow-sm transition-all group"
        data-testid="link-delivery"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-[#EAF3EE] flex items-center justify-center shrink-0">
              <Truck className="w-5 h-5 text-[#1F4E3D]" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-medium text-[#1A1C1A]">Zones & frais de livraison</h2>
              <p className="text-sm text-[#575A57] mt-0.5">
                Configurez les codes postaux locaux, le seuil de livraison gratuite et les tarifs nationaux pour la boutique.
              </p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-[#A3A6A3] group-hover:text-[#1F4E3D] group-hover:translate-x-0.5 transition-all" />
        </div>
      </Link>

      <Link
        to="/parametres/cms-boutique"
        className="block bg-white border border-[#E5E5E0] rounded-lg p-6 hover:border-[#C1A063]/60 hover:shadow-sm transition-all group"
        data-testid="link-shop-cms"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-[#FBF4E9] flex items-center justify-center shrink-0">
              <FileText className="w-5 h-5 text-[#8C6B2F]" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-medium text-[#1A1C1A]">Textes éditables boutique</h2>
              <p className="text-sm text-[#575A57] mt-0.5">
                Modifiez en direct les messages affichés aux visiteurs (délais & fraîcheur, à propos, promo du moment) — sans redéploiement.
              </p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-[#A3A6A3] group-hover:text-[#8C6B2F] group-hover:translate-x-0.5 transition-all" />
        </div>
      </Link>

      <Link
        to="/parametres/photos-boutique"
        className="block bg-white border border-[#E5E5E0] rounded-lg p-6 hover:border-[#1F4E3D]/60 hover:shadow-sm transition-all group"
        data-testid="link-shop-photos"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-[#EAF3EE] flex items-center justify-center shrink-0">
              <ImageIcon className="w-5 h-5 text-[#1F4E3D]" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-medium text-[#1A1C1A]">Photos boutique</h2>
              <p className="text-sm text-[#575A57] mt-0.5">
                Uploadez vous-même le bandeau et les 4 étapes savoir-faire. Détection auto des doublons.
              </p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-[#A3A6A3] group-hover:text-[#1F4E3D] group-hover:translate-x-0.5 transition-all" />
        </div>
      </Link>

      <Link
        to="/parametres/clients-boutique"
        className="block bg-white border border-[#E5E5E0] rounded-lg p-6 hover:border-[#1F4E3D]/40 hover:shadow-sm transition-all group"
        data-testid="link-shop-customers"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-[#EAF3EE] flex items-center justify-center shrink-0">
              <Users className="w-5 h-5 text-[#1F4E3D]" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-medium text-[#1A1C1A]">Clients boutique B2C</h2>
              <p className="text-sm text-[#575A57] mt-0.5">
                Gérez les comptes créés via le formulaire d'inscription : vérification manuelle, désactivation, suppression RGPD.
              </p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-[#A3A6A3] group-hover:text-[#1F4E3D] group-hover:translate-x-0.5 transition-all" />
        </div>
      </Link>

      <Link
        to="/parametres/emails"
        className="block bg-white border border-[#E5E5E0] rounded-lg p-6 hover:border-[#1F4E3D]/40 hover:shadow-sm transition-all group"
        data-testid="link-emails"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-[#EAF3EE] flex items-center justify-center shrink-0">
              <Mail className="w-5 h-5 text-[#1F4E3D]" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-medium text-[#1A1C1A]">Emails de confirmation</h2>
              <p className="text-sm text-[#575A57] mt-0.5">
                Configuration Resend, vérification du domaine, et test d'envoi du mail de confirmation automatique aux clients.
              </p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-[#A3A6A3] group-hover:text-[#1F4E3D] group-hover:translate-x-0.5 transition-all" />
        </div>
      </Link>

      <Link
        to="/parametres/alerte-telegram"
        className="block bg-white border border-[#E5E5E0] rounded-lg p-6 hover:border-[#1F4E3D]/40 hover:shadow-sm transition-all group"
        data-testid="link-telegram-alert"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-[#EAF3EE] flex items-center justify-center shrink-0">
              <BellRing className="w-5 h-5 text-[#1F4E3D]" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-medium text-[#1A1C1A]">Alerte Telegram</h2>
              <p className="text-sm text-[#575A57] mt-0.5">
                Un message sur votre téléphone à chaque commande web payée par PayPal.
              </p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-[#A3A6A3] group-hover:text-[#1F4E3D] group-hover:translate-x-0.5 transition-all" />
        </div>
      </Link>

      <Link
        to="/parametres/brief-matinal"
        className="block bg-white border border-[#E5E5E0] rounded-lg p-6 hover:border-[#1F4E3D]/40 hover:shadow-sm transition-all group"
        data-testid="link-morning-brief"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-[#FBF4E9] flex items-center justify-center shrink-0">
              <Mail className="w-5 h-5 text-[#8C6B2F]" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-medium text-[#1A1C1A]">Brief matinal du terrain</h2>
              <p className="text-sm text-[#575A57] mt-0.5">
                Email automatique envoyé chaque matin (07h00 CH) aux opérateurs de tournée avec leurs tâches du jour : DLC périmées, réapprovisionnements.
              </p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-[#A3A6A3] group-hover:text-[#1F4E3D] group-hover:translate-x-0.5 transition-all" />
        </div>
      </Link>

      <Link
        to="/parametres/alertes-shift"
        className="block bg-white border border-[#E5E5E0] rounded-lg p-6 hover:border-[#C05C47]/40 hover:shadow-sm transition-all group"
        data-testid="link-shift-alerts"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-[#FBEEEA] flex items-center justify-center shrink-0">
              <Bell className="w-5 h-5 text-[#C05C47]" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-medium text-[#1A1C1A]">Alertes stock 3x8 (Novelis)</h2>
              <p className="text-sm text-[#575A57] mt-0.5">
                Email automatique 30 min avant chaque changement d'équipe (05h30 / 13h30 / 21h30 CH) listant les slots à ≤ 2 unités pour anti-rupture continue 24/7.
              </p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-[#A3A6A3] group-hover:text-[#C05C47] group-hover:translate-x-0.5 transition-all" />
        </div>
      </Link>

      <Link
        to="/parametres/emballages"
        className="block bg-white border border-[#E5E5E0] rounded-lg p-6 hover:border-[#1F4E3D]/40 hover:shadow-sm transition-all group"
        data-testid="link-packaging-consumables"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-[#EAF3EE] flex items-center justify-center shrink-0">
              <Package className="w-5 h-5 text-[#1F4E3D]" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-medium text-[#1A1C1A]">Consommables emballage</h2>
              <p className="text-sm text-[#575A57] mt-0.5">
                Barquettes, films opercule, étiquettes, gaz : suivi des prix d'achat et calcul automatique du coût d'emballage par unité dans le prix de revient.
              </p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-[#A3A6A3] group-hover:text-[#1F4E3D] group-hover:translate-x-0.5 transition-all" />
        </div>
      </Link>

      <Link
        to="/parametres/email-prospection"
        className="block bg-white border border-[#E5E5E0] rounded-lg p-6 hover:border-[#1F4E3D]/40 hover:shadow-sm transition-all group"
        data-testid="link-email-prospection"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-[#FBF4E9] flex items-center justify-center shrink-0">
              <Mail className="w-5 h-5 text-[#8C6B2F]" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-medium text-[#1A1C1A]">Prospection B2B manuelle</h2>
              <p className="text-sm text-[#575A57] mt-0.5">
                Générateur d'email personnalisé pour vos prospects revendeurs. Copiez le HTML dans Mailspring, envoyez, puis tracez l'envoi. Pas de campagne masse — un prospect à la fois.
              </p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-[#A3A6A3] group-hover:text-[#1F4E3D] group-hover:translate-x-0.5 transition-all" />
        </div>
      </Link>


      {/* Phase 87aw — 24/06/2026 : entrées XLSX/CSV Nayax cachées de la liste
          Settings (sync API Lynx prod active depuis aujourd'hui). Les routes
          /parametres/import-nayax-xlsx et /parametres/import-nayax-csv restent
          accessibles via URL directe en fallback d'urgence si la sync API
          tombe. À supprimer définitivement après 2-3 mois d'observation prod
          si la sync API se stabilise sans intermittence. */}

      <button
        onClick={downloadFieldOpsGuide}
        disabled={downloadingGuide}
        className="w-full text-left bg-white border border-[#E5E5E0] rounded-lg p-6 hover:border-[#1F4E3D]/40 hover:shadow-sm transition-all group"
        data-testid="link-fieldops-guide"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-[#FBF4E9] flex items-center justify-center shrink-0">
              {downloadingGuide ? <Loader2 className="w-5 h-5 text-[#8C6B2F] animate-spin" /> : <FileText className="w-5 h-5 text-[#8C6B2F]" />}
            </div>
            <div>
              <h2 className="font-heading text-xl font-medium text-[#1A1C1A]">Guide PDF — Procédure tournée matin</h2>
              <p className="text-sm text-[#575A57] mt-0.5">
                Fiche de poche imprimable (3 pages A4) pour vos opérateurs terrain : checklist avant départ, 4 étapes par distributeur, 5 motifs de retrait HACCP, procédure import CSV Nayax. À distribuer aux 3 collaborateurs.
              </p>
            </div>
          </div>
          <Download className="w-5 h-5 text-[#A3A6A3] group-hover:text-[#1F4E3D] transition-all" />
        </div>
      </button>


      <div className="bg-white border border-[#E5E5E0] rounded-lg p-6">
        <div className="flex items-center gap-2 mb-5">
          <Building2 className="w-5 h-5 text-[#1F4E3D]" />
          <h2 className="font-heading text-xl font-medium">Production</h2>
        </div>
        <div className="space-y-4">
          <div>
            <Label className="mb-1.5 block">Dépôt d'arrivée après clôture d'un OF</Label>
            <Select
              value={form.production_depot_id || "none"}
              onValueChange={(v) => setForm({ ...form, production_depot_id: v === "none" ? null : v })}
            >
              <SelectTrigger data-testid="production-depot"><SelectValue placeholder="Aucun" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucun (auto-placement désactivé)</SelectItem>
                {distributors.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name}{d.code ? ` (${d.code})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-[#575A57] mt-1.5">
              À la clôture d'un ordre de fabrication, chaque barquette produite est automatiquement créée
              dans ce dépôt. Si le dépôt a des emplacements actifs, les barquettes sont placées dans les
              emplacements libres ; sinon elles restent "en attente" pour transfert ultérieur.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-[#E5E5E0] rounded-lg p-6">
        <div className="flex items-center gap-2 mb-5">
          <Building2 className="w-5 h-5 text-[#1F4E3D]" />
          <h2 className="font-heading text-xl font-medium">Informations entreprise</h2>
        </div>
        <div className="space-y-4">
          <div>
            <Label className="mb-1.5 block">Nom commercial (raison sociale légale complète)</Label>
            <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="company-name" placeholder="Maison Thaï Pongsak Phimrat" />
            <p className="text-xs text-[#575A57] mt-1.5">Utilisé pour le <strong>Bulletin de paiement (QR-Bill)</strong> et les documents légaux. Pour une raison individuelle suisse, doit contenir Prénom + Nom du gérant.</p>
          </div>
          <div>
            <Label className="mb-1.5 block">Nom court affiché en en-tête PDF (facultatif)</Label>
            <Input value={form.company_display_name || ""} onChange={(e) => setForm({ ...form, company_display_name: e.target.value })} data-testid="company-display-name" placeholder="Maison Thaï" />
            <p className="text-xs text-[#575A57] mt-1.5">Nom compact utilisé <strong>uniquement</strong> dans l&apos;en-tête PDF des devis / factures / étiquettes (pour éviter que le nom complet légal prenne trop de place). Si vide, on utilise le nom commercial ci-dessus.</p>
          </div>
          <div>
            <Label className="mb-1.5 block">Adresse</Label>
            <Textarea rows={2} value={form.company_address} onChange={(e) => setForm({ ...form, company_address: e.target.value })} data-testid="company-address" placeholder="Rue de Parcouret 22" />
            <p className="text-xs text-[#575A57] mt-1.5">Rue uniquement. NPA + ville sont saisis ci-dessous (concaténés sur les étiquettes).</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="mb-1.5 block">NPA</Label>
              <Input value={form.company_postal_code || ""} onChange={(e) => setForm({ ...form, company_postal_code: e.target.value })} placeholder="1964" data-testid="company-postal" />
            </div>
            <div>
              <Label className="mb-1.5 block">Ville</Label>
              <Input value={form.company_city || ""} onChange={(e) => setForm({ ...form, company_city: e.target.value })} placeholder="Conthey" data-testid="company-city" />
            </div>
          </div>
          <div>
            <Label className="mb-1.5 block">Téléphone</Label>
            <Input
              value={form.company_phone || ""}
              onChange={(e) => setForm({ ...form, company_phone: e.target.value })}
              placeholder="+41 27 346 07 75"
              data-testid="company-phone"
            />
            <p className="text-xs text-[#575A57] mt-1.5">Affiché sous l'adresse sur les étiquettes et factures.</p>
          </div>
          <div>
            <Label className="mb-1.5 block">Mentions légales (obligatoires)</Label>
            <Textarea
              rows={3}
              value={form.legal_mentions || ""}
              onChange={(e) => setForm({ ...form, legal_mentions: e.target.value })}
              placeholder={"Fabrication artisanale\nConditionné sous atmosphère protectrice (MAP)"}
              data-testid="legal-mentions"
            />
            <p className="text-xs text-[#575A57] mt-1.5">
              Une mention par ligne. Affiché en bas de chaque étiquette (au-dessus du lot/DLC). Conforme art. 35 ODAlOUs.
            </p>
          </div>
          <div>
            <Label className="mb-1.5 block">Logo (PNG / JPG / SVG, max 1 Mo)</Label>
            <div className="flex items-start gap-4">
              <div className="w-24 h-24 border border-dashed border-[#C9CFC9] rounded-md flex items-center justify-center bg-[#F9F9F6] overflow-hidden shrink-0" data-testid="logo-preview">
                {form.company_logo ? (
                  <img src={form.company_logo} alt="Logo" className="max-w-full max-h-full object-contain" />
                ) : (
                  <ImageIcon className="w-7 h-7 text-[#C9CFC9]" />
                )}
              </div>
              <div className="flex-1 space-y-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                  data-testid="logo-input"
                />
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} data-testid="upload-logo-btn">
                    <Upload className="w-4 h-4 mr-2" /> {form.company_logo ? "Remplacer" : "Choisir un fichier"}
                  </Button>
                  {form.company_logo && (
                    <Button variant="ghost" size="sm" onClick={removeLogo} data-testid="remove-logo-btn">
                      <X className="w-4 h-4 mr-1" /> Retirer
                    </Button>
                  )}
                </div>
                <p className="text-xs text-[#575A57]">Affiché sur les étiquettes (à gauche du nom commercial). Format carré conseillé.</p>
              </div>
            </div>
          </div>
          {/* Phase 101dd — Signature scannée pour les courriers */}
          <div className="md:col-span-2">
            <Label className="mb-1.5 block">Signature scannée (PNG transparent conseillé, max 1 Mo)</Label>
            <div className="flex items-start gap-4">
              <div className="w-40 h-20 border border-dashed border-[#C9CFC9] rounded-md flex items-center justify-center bg-[#F9F9F6] overflow-hidden shrink-0" data-testid="signature-preview">
                {form.company_signature_image ? (
                  <img src={form.company_signature_image} alt="Signature" className="max-w-full max-h-full object-contain" />
                ) : (
                  <ImageIcon className="w-7 h-7 text-[#C9CFC9]" />
                )}
              </div>
              <div className="flex-1 space-y-2">
                <input
                  ref={signatureInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleSignatureUpload}
                  className="hidden"
                  data-testid="signature-input"
                />
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => signatureInputRef.current?.click()} data-testid="upload-signature-btn">
                    <Upload className="w-4 h-4 mr-2" /> {form.company_signature_image ? "Remplacer" : "Choisir un fichier"}
                  </Button>
                  {form.company_signature_image && (
                    <Button variant="ghost" size="sm" onClick={removeSignature} data-testid="remove-signature-btn">
                      <X className="w-4 h-4 mr-1" /> Retirer
                    </Button>
                  )}
                </div>
                <p className="text-xs text-[#575A57]">
                  Utilisée dans les courriers (au-dessus du nom du signataire) lorsque l&apos;option
                  <em> « Inclure signature scannée » </em> est activée sur la lettre.
                  PNG transparent avec la seule signature à l&apos;encre foncée donne le meilleur rendu.
                </p>
              </div>
            </div>
          </div>
          <div>
            <Label className="mb-1.5 block">Nom(s) du/des signataire(s)</Label>
            <Textarea
              rows={2}
              value={form.company_signature_name || ""}
              onChange={(e) => setForm((f) => ({ ...f, company_signature_name: e.target.value }))}
              placeholder="Alain Phimrat&#10;Sokchea Phimrat"
              data-testid="signature-name-input"
            />
            <p className="text-xs text-[#575A57] mt-1.5">
              Un nom par ligne (2 max recommandé) — affiché dans les courriers sous « Maison Thaï Conthey ».
            </p>
          </div>
          <div>
            <Label className="mb-1.5 block">Rôle / fonction (optionnel)</Label>
            <Input
              value={form.company_signature_role || ""}
              onChange={(e) => setForm((f) => ({ ...f, company_signature_role: e.target.value }))}
              placeholder="Direction · gérant · responsable production…"
              data-testid="signature-role-input"
            />
            <p className="text-xs text-[#575A57] mt-1.5">
              Affiché en petit gris sous le(s) nom(s) dans les courriers.
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="save-settings-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Enregistrer
        </Button>
      </div>

      {/* Facturation suisse QR-Bill */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg p-6" data-testid="invoicing-zone">
        <div className="flex items-center gap-2 mb-1">
          <h2 className="text-base font-medium text-[#1A1C1A]">Facturation suisse · QR-Bill</h2>
        </div>
        <p className="text-xs text-[#575A57] mb-4">Coordonnées bancaires et fiscales utilisées pour générer le QR-code de paiement ISO 20022.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="mb-1.5 block">IBAN ou QR-IBAN *</Label>
            <Input value={form.company_iban || ""} onChange={(e) => setForm({ ...form, company_iban: e.target.value.replace(/\s+/g, "").toUpperCase() })} placeholder="CH4431999123000889012" data-testid="company-iban" />
            <p className="text-xs text-[#A3A6A3] mt-1">Sans espaces. Le QR-IBAN suisse commence souvent par CH3.</p>
          </div>
          <div>
            <Label className="mb-1.5 block">Numéro TVA</Label>
            <Input value={form.company_vat_number || ""} onChange={(e) => setForm({ ...form, company_vat_number: e.target.value })} placeholder="CHE-123.456.789" data-testid="company-vat" />
          </div>
          <div>
            <Label className="mb-1.5 block">Type de référence QR</Label>
            <select
              value={form.qr_reference_type || "SCOR"}
              onChange={(e) => setForm({ ...form, qr_reference_type: e.target.value })}
              className="flex h-10 w-full rounded-md border border-[#E5E5E0] bg-white px-3 text-sm"
              data-testid="qr-ref-type"
            >
              <option value="SCOR">SCOR — Référence créancier (RFxx) — recommandé</option>
              <option value="QRR">QR-Reference — 27 chiffres (uniquement avec QR-IBAN)</option>
              <option value="NON">Sans référence</option>
            </select>
          </div>
          <div>
            <Label className="mb-1.5 block">Taux TVA par défaut (%)</Label>
            <Input type="number" step="0.1" value={form.default_vat_rate ?? 2.6} onChange={(e) => setForm({ ...form, default_vat_rate: parseFloat(e.target.value) || 0 })} data-testid="default-vat" />
            <p className="text-xs text-[#A3A6A3] mt-1">2.6% denrées alimentaires en Suisse depuis 2024 · 8.1% taux normal.</p>
          </div>
          <div>
            <Label className="mb-1.5 block">Délai de paiement (jours)</Label>
            <Input type="number" min="0" value={form.payment_terms_days ?? 30} onChange={(e) => setForm({ ...form, payment_terms_days: parseInt(e.target.value) || 0 })} data-testid="payment-days" />
          </div>
          <div>
            <Label className="mb-1.5 block">Conditions de paiement (texte affiché)</Label>
            <Input value={form.payment_terms_text || ""} onChange={(e) => setForm({ ...form, payment_terms_text: e.target.value })} placeholder="30 jours net" data-testid="payment-text" />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <Button onClick={save} disabled={saving} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="save-invoicing-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Enregistrer
          </Button>
        </div>
      </div>

      {/* Formulaires administratifs VS (Police du Commerce + SCAV) */}
      <ScavCantonSection form={form} setForm={setForm} save={save} saving={saving} />

      {/* Planification de production */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg p-6" data-testid="production-zone">
        <h2 className="text-base font-medium text-[#1A1C1A] mb-1">Planification de production</h2>
        <p className="text-xs text-[#575A57] mb-4">Paramètres utilisés par le tableau de bord pour calculer le besoin de production anticipée.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="mb-1.5 block">Buffer d'anticipation (%)</Label>
            <Input type="number" min="0" max="200" value={form.production_buffer_percent ?? 30} onChange={(e) => setForm({ ...form, production_buffer_percent: parseInt(e.target.value) || 0 })} data-testid="prod-buffer-pct" />
            <p className="text-xs text-[#A3A6A3] mt-1">Au-dessus de la demande des commandes en attente. 30 % par défaut.</p>
          </div>
          <div>
            <Label className="mb-1.5 block">Stock minimum à maintenir (unités)</Label>
            <Input type="number" min="0" value={form.min_stock_threshold ?? 0} onChange={(e) => setForm({ ...form, min_stock_threshold: parseInt(e.target.value) || 0 })} data-testid="prod-min-stock" />
            <p className="text-xs text-[#A3A6A3] mt-1">Si stock &lt; ce seuil, une production sera suggérée même sans commande.</p>
          </div>
          <div>
            <Label className="mb-1.5 block">Seuil marge minimum (%)</Label>
            <Input
              type="number"
              min="0"
              max="100"
              step="1"
              value={form.min_margin_percent ?? 30}
              onChange={(e) =>
                setForm({ ...form, min_margin_percent: parseFloat(e.target.value) || 0 })
              }
              data-testid="min-margin-percent"
            />
            <p className="text-xs text-[#A3A6A3] mt-1">
              Une alerte rouge ⚠️ s'affiche sur la fiche produit et dans la liste pour toute marge sous ce seuil. 30 % par défaut.
            </p>
          </div>
          <div className="md:col-span-2">
            <Label className="mb-1.5 block">Préfixe GS1 société (GS1 Switzerland) — 6 à 11 chiffres</Label>
            <Input
              type="text"
              value={form.ean13_prefix ?? ""}
              onChange={(e) =>
                setForm({ ...form, ean13_prefix: e.target.value.replace(/\D/g, "").slice(0, 11) })
              }
              placeholder="ex : 7649998205"
              maxLength={11}
              className="font-mono"
              data-testid="ean13-prefix"
            />
            <p className="text-xs text-[#A3A6A3] mt-1">
              Votre préfixe d&apos;entreprise attribué par GS1 Switzerland (dérivé de votre GLN).
              <br />
              Capacité produits = 10^(12 − longueur préfixe). Ex : préfixe 10 chiffres → 100 produits, 9 chiffres → 1000, 8 chiffres → 10 000.
              <br />
              Sert à pré-remplir automatiquement le champ EAN13 des nouveaux produits (séquence incrémentale).
              Vide → aucune suggestion automatique.
            </p>
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <Button onClick={save} disabled={saving} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="save-prod-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Enregistrer
          </Button>
        </div>
      </div>

      {/* Clés API */}
      <ApiKeysManager />

      {/* Phase 102 — Réseaux sociaux boutique */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg p-6" data-testid="social-links-zone">
        <h2 className="text-base font-medium text-[#1A1C1A] mb-1">Boutique · réseaux sociaux</h2>
        <p className="text-xs text-[#575A57] mb-4">
          URLs affichées en pied de page de la boutique publique (`/boutique`). Laissez vide pour masquer l&apos;icône correspondante.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="mb-1.5 block flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-[#1877F2] text-white text-[10px] font-bold">f</span>
              Facebook (URL complète)
            </Label>
            <Input
              type="url"
              value={form.shop_facebook_url ?? ""}
              onChange={(e) => setForm({ ...form, shop_facebook_url: e.target.value })}
              placeholder="https://www.facebook.com/1083167851556707"
              data-testid="shop-facebook-url"
            />
            <p className="text-xs text-[#A3A6A3] mt-1">
              Format accepté : `https://www.facebook.com/PageName` ou `https://www.facebook.com/PageID`.
            </p>
          </div>
          <div>
            <Label className="mb-1.5 block flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] text-white text-[10px] font-bold">ig</span>
              Instagram (URL complète)
            </Label>
            <Input
              type="url"
              value={form.shop_instagram_url ?? ""}
              onChange={(e) => setForm({ ...form, shop_instagram_url: e.target.value })}
              placeholder="https://www.instagram.com/votre_compte"
              data-testid="shop-instagram-url"
            />
            <p className="text-xs text-[#A3A6A3] mt-1">
              Vide = icône Instagram masquée. Vous pourrez l&apos;ajouter plus tard.
            </p>
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <Button
            onClick={save}
            disabled={saving}
            className="bg-[#1F4E3D] hover:bg-[#173B2E]"
            data-testid="save-social-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Enregistrer
          </Button>
        </div>
      </div>

      {/* Phase 101ey — TWINT Payment Link (boutique B2C) */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg p-6" data-testid="twint-zone">
        <div className="flex items-start justify-between mb-1">
          <div>
            <h2 className="text-base font-medium text-[#1A1C1A] mb-1 flex items-center gap-2">
              <span className="inline-flex items-center justify-center px-2 py-0.5 rounded bg-[#EE1E5A] text-white text-[10px] font-bold tracking-wider">TWINT</span>
              Paiement mobile suisse (boutique B2C)
            </h2>
            <p className="text-xs text-[#575A57]">
              Mode « sans chichi » : lien statique fourni par ton TWINT Business Portal.
              <br />
              Le client scanne un QR ou clique sur mobile → il paye. <strong>Aucune confirmation automatique</strong> :
              tu marques la commande comme payée manuellement depuis sa fiche après vérification dans ton compte TWINT.
            </p>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={Boolean(form.twint_enabled)}
              onChange={(e) => setForm({ ...form, twint_enabled: e.target.checked })}
              data-testid="twint-enabled-toggle"
            />
            <span className="text-xs font-medium text-[#1A1C1A]">Activer TWINT</span>
          </label>
        </div>
        <div className="mt-4">
          <Label className="mb-1.5 block">Lien TWINT (fourni par TWINT Business Portal)</Label>
          <Input
            type="url"
            value={form.twint_payment_link ?? ""}
            onChange={(e) => setForm({ ...form, twint_payment_link: e.target.value })}
            placeholder="https://go.twint.ch/1/e/tw?tw=acq.XXXXXX"
            data-testid="twint-payment-link"
          />
          <p className="text-xs text-[#A3A6A3] mt-1">
            Format attendu : `https://go.twint.ch/1/e/tw?tw=acq.…`. Ce lien est <strong>propre à ton compte marchand</strong> —
            ne le partage pas publiquement.
          </p>
        </div>
        <div className="flex justify-end mt-4">
          <Button
            onClick={save}
            disabled={saving}
            className="bg-[#EE1E5A] hover:bg-[#D01A50] text-white"
            data-testid="save-twint-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Enregistrer TWINT
          </Button>
        </div>
      </div>

      {/* Synchro Nayax — Phase 87ax */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg p-6" data-testid="nayax-zone">
        <h2 className="text-base font-medium text-[#1A1C1A] mb-1">Synchro Nayax · cutoff global</h2>
        <p className="text-xs text-[#575A57] mb-4">
          Date de coupure unique appliquée à <strong>tous les distributeurs</strong> : toute vente Nayax antérieure à cette date est ignorée (ni mouvement stock, ni doublon). Utile pour reprendre proprement la sync sans repolluer l'historique avec d'anciennes ventes.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="mb-1.5 block">Date de cutoff (à partir de minuit UTC)</Label>
            <Input
              type="date"
              value={form.nayax_sync_start_date || ""}
              onChange={(e) => setForm({ ...form, nayax_sync_start_date: e.target.value })}
              data-testid="nayax-sync-start-date"
            />
            <p className="text-xs text-[#A3A6A3] mt-1">
              Vide = aucun cutoff (toutes les ventes remontées par l'API sont traitées, soit env. 30 jours en arrière).
              <br />
              Conseil : pour stopper l'hémorragie de doublons, définissez la date d'aujourd'hui.
            </p>
          </div>
          <div className="flex items-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                const today = new Date().toISOString().slice(0, 10);
                setForm({ ...form, nayax_sync_start_date: today });
              }}
              data-testid="nayax-cutoff-today-btn"
            >
              Mettre à aujourd'hui
            </Button>
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <Button onClick={save} disabled={saving} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="save-nayax-cutoff-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Enregistrer
          </Button>
        </div>

        {/* Phase 98y — Lien vers l'historique des syncs Nayax */}
        <div className="mt-6 pt-4 border-t border-[#E5E5E0] flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-medium text-[#1A1C1A]">Historique des syncs & diagnostics Nayax</h3>
            <p className="text-xs text-[#575A57] mt-0.5">
              Journal des appels pickList (verrouillage tournée, sync manuelle, probe test-write). Rétention 30 jours.
            </p>
          </div>
          <Link to="/parametres/nayax-sync-logs">
            <Button variant="outline" size="sm" data-testid="nayax-sync-logs-link">
              Ouvrir l'historique
            </Button>
          </Link>
        </div>

        {/* Phase 101bb — Mode expert push picklist Nayax (masqué par défaut) */}
        <div className="mt-6 pt-4 border-t border-[#E5E5E0]" data-testid="nayax-expert-push-zone">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-sm font-medium text-[#1A1C1A]">Mode expert — push picklist Nayax</h3>
              <p className="text-xs text-[#575A57] mt-0.5 leading-relaxed">
                Par défaut <strong>désactivé</strong> — les picklists sont imprimées depuis l&apos;interface Nayax (source de vérité MOMA).
                Active uniquement en cas d&apos;intervention technique pour rejouer manuellement un <code className="text-[10px] bg-[#F4F4F0] px-1 py-0.5 rounded">PUT /picklists/update</code> vers Nayax
                (bouton « Sync tous les MOMA » réapparaît sur la page Distributeurs).
              </p>
            </div>
            <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
              <input
                type="checkbox"
                checked={Boolean(form.nayax_expert_push_mode)}
                onChange={(e) => setForm({ ...form, nayax_expert_push_mode: e.target.checked })}
                data-testid="nayax-expert-push-toggle"
                className="w-4 h-4 accent-[#8C6B2F]"
              />
              <span className="text-xs font-medium text-[#8C6B2F]">
                {form.nayax_expert_push_mode ? "Activé" : "Désactivé"}
              </span>
            </label>
          </div>
          <div className="flex justify-end mt-3">
            <Button
              onClick={save}
              disabled={saving}
              size="sm"
              variant="outline"
              className="border-[#8C6B2F] text-[#8C6B2F] hover:bg-[#FBF4E9]"
              data-testid="save-nayax-expert-push-btn"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
              Enregistrer
            </Button>
          </div>
        </div>
      </div>

      {/* Sauvegarde & Restauration */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg p-6" data-testid="backup-zone">
        <div className="flex items-center gap-2 mb-2">
          <Database className="w-5 h-5 text-[#1F4E3D]" />
          <h2 className="font-heading text-xl font-medium">Sauvegarde & Restauration</h2>
        </div>
        <p className="text-sm text-[#575A57] mb-4">
          Exportez régulièrement vos données (produits, dépôts, lots, OF, inventaires, mouvements, historique impressions)
          dans un fichier JSON. Vous pourrez les restaurer à tout moment depuis ce même écran.
        </p>
        <div className="flex flex-wrap gap-3">
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={exporting}
            data-testid="export-backup-btn"
          >
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Exporter la sauvegarde (JSON)
          </Button>
          <input
            ref={backupInputRef}
            type="file"
            accept=".json,application/json"
            onChange={handleBackupSelected}
            className="hidden"
            data-testid="backup-input"
          />
          <Button
            variant="outline"
            onClick={() => backupInputRef.current?.click()}
            data-testid="import-backup-btn"
          >
            <FileUp className="w-4 h-4 mr-2" /> Restaurer depuis un fichier…
          </Button>
        </div>
        <p className="text-xs text-[#575A57] mt-3">
          Astuce : sauvegardez avant toute opération sensible (nettoyage, fin de mois, tests).
        </p>
      </div>

      {/* Dialogue de confirmation d'import */}
      <AlertDialog open={!!importPreview} onOpenChange={(o) => { if (!o) setImportPreview(null); }}>
        <AlertDialogContent data-testid="import-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Restaurer la sauvegarde ?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <div>
                  Fichier analysé{importPreview?.exported_at ? ` (export du ${new Date(importPreview.exported_at).toLocaleString("fr-FR")})` : ""} :
                </div>
                {importPreview && (
                  <ul className="text-sm font-mono bg-[#F9F9F6] border border-[#E5E5E0] rounded p-3 space-y-0.5">
                    {Object.entries(importPreview.counts).map(([k, v]) => (
                      <li key={k}><span className="text-[#575A57]">{k}</span> : <span className="font-bold">{v}</span></li>
                    ))}
                  </ul>
                )}
                <div className="space-y-2 pt-2">
                  <div className="font-medium text-[#1A1C1A]">Mode d'import :</div>
                  <label className="flex items-start gap-2 cursor-pointer text-sm">
                    <input
                      type="radio"
                      name="importMode"
                      value="merge"
                      checked={importMode === "merge"}
                      onChange={() => setImportMode("merge")}
                      data-testid="import-mode-merge"
                      className="mt-1"
                    />
                    <span>
                      <strong>Fusion</strong> (recommandé) — ajoute les enregistrements manquants et met à jour ceux qui existent déjà. Les données actuelles non présentes dans la sauvegarde sont conservées.
                    </span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer text-sm">
                    <input
                      type="radio"
                      name="importMode"
                      value="replace"
                      checked={importMode === "replace"}
                      onChange={() => setImportMode("replace")}
                      data-testid="import-mode-replace"
                      className="mt-1"
                    />
                    <span className="text-[#C05C47]">
                      <strong>Remplacement total</strong> — efface toutes les collections avant restauration. <u>Irréversible</u>.
                    </span>
                  </label>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="import-cancel-btn">Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmImport}
              disabled={importing}
              className="bg-[#1F4E3D] hover:bg-[#173B2E]"
              data-testid="import-confirm-btn"
            >
              {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileUp className="w-4 h-4 mr-2" />}
              Confirmer la restauration
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="bg-white border border-[#E5E5E0] rounded-lg p-6" data-testid="maintenance-tools">
        <div className="flex items-center gap-2 mb-2">
          <RotateCcw className="w-5 h-5 text-[#8C6B2F]" />
          <h2 className="font-heading text-xl font-medium text-[#1A1C1A]">Outils de maintenance</h2>
        </div>
        <p className="text-sm text-[#575A57] mb-4">
          Réparation de données et migrations ponctuelles. Toujours commencer par un aperçu (dry-run) avant d'appliquer.
        </p>
        <div className="flex flex-wrap gap-2">
          <FixMissingDlcDialog />
          <FixOrphanLotsDialog />
          <TranslateProductsDialog />
          {/* Phase 87au — Backfill date fab sur BLs / labtests historiques */}
          <Button
            variant="outline"
            size="sm"
            onClick={backfillProductionDates}
            disabled={backfillingDates}
            className="border-[#8C6B2F] text-[#8C6B2F] hover:bg-[#FDF3E0]"
            data-testid="backfill-prod-date-btn"
            title="Renseigne la date de fabrication sur les BLs et tests labo historiques (avant Phase 87au). Idempotent."
          >
            {backfillingDates ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Wrench className="w-4 h-4 mr-2" />
            )}
            Rétro-backfill date fab. BLs & labos
          </Button>
          {/* Phase 87am — Nettoyage lots fantômes shop-online-virtual */}
          <Button
            variant="outline"
            size="sm"
            onClick={cleanupGhostLots}
            disabled={cleaningGhosts}
            className="border-[#8C6B2F] text-[#8C6B2F] hover:bg-[#FDF3E0]"
            data-testid="cleanup-ghost-lots-btn"
            title="Régularise les lots coincés sur le distributeur virtuel « Boutique en ligne » (ancien bug Phase 87am). Idempotent."
          >
            {cleaningGhosts ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Wrench className="w-4 h-4 mr-2" />
            )}
            Nettoyer lots fantômes B2C web
          </Button>
        </div>

        {/* Phase 101ds — Audit & restauration WASTE auto */}
        <div className="mt-6 pt-6 border-t border-[#E5E5E0]">
          <CronWasteAuditPanel />
        </div>

        {/* Phase 101dt — Récupération barquettes sorties sans transit Conthey */}
        <div className="mt-6 pt-6 border-t border-[#E5E5E0]">
          <AdjustAuditPanel />
        </div>

        {/* Phase 101dw.3 — File d'arbitrage automatique des ventes suspectes */}
        <div className="mt-6 pt-6 border-t border-[#E5E5E0]">
          <SalesReviewQueuePanel />
        </div>

        {/* Phase 101dw — Nettoyage ventes fantômes Nayax (tx annulées machine) */}
        <div className="mt-6 pt-6 border-t border-[#E5E5E0]">
          <PhantomNayaxSalesPanel />
        </div>
      </div>

      <div className="bg-white border border-red-200 rounded-lg p-6" data-testid="danger-zone">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="w-5 h-5 text-red-600" />
          <h2 className="font-heading text-xl font-medium text-red-700">Zone de danger</h2>
        </div>

        <div className="space-y-5">
          <div>
            <p className="text-sm text-[#575A57] mb-3">
              <strong>Mise en production</strong> — Reset sélectif et complet de toutes les
              données opérationnelles (ventes, lots, commandes B2C, sessions de réassort, etc.).
              Conserve le référentiel (produits, distributeurs, ingrédients, fournisseurs, utilisateurs).
              Avec aperçu (dry-run) avant suppression.
            </p>
            <ResetProductionDialog />
          </div>

          <div className="pt-4 border-t border-red-100">
            <p className="text-sm text-[#575A57] mb-3">
              <strong>Reset rapide</strong> — Remet à zéro les opérations principales :
              <strong> lots, mouvements de stock, ordres de fabrication, inventaires
              et historique d'impression</strong>. Les produits et dépôts/distributeurs sont conservés.
              Cette action est irréversible.
            </p>
            <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="destructive"
              disabled={resetting}
              data-testid="reset-operations-btn"
            >
              {resetting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RotateCcw className="w-4 h-4 mr-2" />
              )}
              Mettre à zéro les opérations
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid="reset-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Confirmer la remise à zéro</AlertDialogTitle>
              <AlertDialogDescription>
                Tous les lots, mouvements, ordres de fabrication et inventaires seront définitivement
                supprimés. Les produits et dépôts seront conservés. Souhaitez-vous continuer ?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="reset-cancel-btn">Annuler</AlertDialogCancel>
              <AlertDialogAction
                onClick={resetOperations}
                className="bg-red-600 hover:bg-red-700"
                data-testid="reset-confirm-btn"
              >
                Confirmer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
          </div>
        </div>
      </div>

      {/* Phase 101dg (Feb 2026) — Verrou expert Nayax : bascule protégée */}
      <NayaxExpertModeCard
        settings={form}
        onChange={(enabled) => setForm((f) => ({ ...f, nayax_expert_push_mode: enabled }))}
      />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * Phase 101dg — Zone dangereuse : Mode expert Nayax push
 * ─────────────────────────────────────────────────────────────────
 * Contexte : par défaut AgroManage est READ-ONLY vis-à-vis de Nayax
 * (aucun push, aucune écriture). 3 endpoints avancés existent pour
 * synchroniser AM → Nayax (create_transfer, resync-all-momas, push-picklist)
 * mais ils sont verrouillés derrière `nayax_expert_push_mode` (Phase 101cz-gate).
 *
 * Cette carte ajoute une UI protégée pour basculer ce flag :
 *  - Toggle avec état actuel visible
 *  - Activation → dialog forçant à taper "ACTIVER" (verbatim)
 *  - Désactivation → simple confirmation (kill-switch immédiat)
 *  - Bandeau rouge global apparaît/disparaît via un event global
 *    (`nayax-expert-mode-changed`) écouté par Layout.jsx
 */
function NayaxExpertModeCard({ settings, onChange }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [saving, setSaving] = useState(false);
  const isEnabled = !!settings?.nayax_expert_push_mode;

  const handleToggle = () => {
    setConfirmText("");
    setDialogOpen(true);
  };

  const handleConfirm = async () => {
    setSaving(true);
    try {
      const willEnable = !isEnabled;
      const body = willEnable
        ? { enabled: true, confirmation: confirmText }
        : { enabled: false };
      const { data } = await api.put("/settings/nayax-expert-mode", body);
      onChange(!!data.nayax_expert_push_mode);
      toast[willEnable ? "warning" : "success"](
        data.message || (willEnable ? "Mode expert activé" : "Mode expert désactivé"),
        { duration: 6000 }
      );
      // Notifie Layout.jsx pour rafraîchir la bannière immédiatement
      window.dispatchEvent(new CustomEvent("nayax-expert-mode-changed", {
        detail: { enabled: !!data.nayax_expert_push_mode },
      }));
      setDialogOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de bascule mode expert");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`border rounded-lg p-6 ${isEnabled ? "border-[#C05C47] bg-[#FCEEEA]" : "border-[#E5E5E0] bg-white"}`}
      data-testid="nayax-expert-mode-card"
    >
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="flex-1 min-w-[280px]">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className={`w-5 h-5 ${isEnabled ? "text-[#C05C47]" : "text-[#8B7A2B]"}`} />
            <h2 className="text-base font-medium text-[#1A1C1A]">
              Mode expert Nayax — écritures autorisées
            </h2>
            {isEnabled && (
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-[#C05C47] text-white font-semibold">
                ACTIF
              </span>
            )}
          </div>
          <p className="text-xs text-[#575A57] leading-relaxed max-w-2xl">
            Par défaut, AgroManage ne fait <strong>aucune écriture vers Nayax</strong>{" "}
            (lecture seule des ventes et du stock machine). Activer ce mode
            autorise les 3 endpoints suivants à pousser des données vers Nayax :
          </p>
          <ul className="text-xs text-[#575A57] list-disc list-inside mt-2 ml-1 space-y-0.5">
            <li><strong>Créer transfert MOMA</strong> — mise à jour de la matrice picklist Nayax</li>
            <li><strong>Resync-all-momas-latest-tour</strong> — pousse la dernière tournée AM vers toutes les machines</li>
            <li><strong>Debug push-picklist</strong> — envoi manuel d'une picklist pour tests</li>
          </ul>
          <p className="text-xs text-[#575A57] mt-3 italic">
            ⚠ N'activez ce mode que si vous savez précisément ce que vous faites.
            Une manœuvre incorrecte peut modifier la configuration des distributeurs.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-xs font-medium text-[#1A1C1A]">
              {isEnabled ? "🔓 Autorisé" : "🔒 Verrouillé"}
            </span>
            <Switch
              checked={isEnabled}
              onCheckedChange={handleToggle}
              data-testid="nayax-expert-mode-toggle"
            />
          </div>
        </div>
      </div>

      <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <AlertDialogContent data-testid="nayax-expert-mode-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isEnabled ? "Désactiver le mode expert ?" : "Activer le mode expert Nayax ?"}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-[#575A57] leading-relaxed">
                {isEnabled ? (
                  <div className="space-y-2">
                    <div>
                      Vous êtes sur le point de <strong>désactiver</strong> les écritures
                      Nayax. AgroManage repassera en mode lecture seule immédiatement.
                    </div>
                    <div className="text-[#1F4E3D]">
                      ✓ C'est la position sécurisée par défaut. Aucune confirmation
                      supplémentaire n'est requise.
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      Vous allez <strong className="text-[#C05C47]">autoriser AgroManage à écrire</strong> sur
                      la plateforme Nayax. Toute action déclenchée par un membre du staff
                      pourra <strong>modifier la configuration des distributeurs</strong>{" "}
                      (matrices, picklists, transferts).
                    </div>
                    <div className="border border-[#C05C47] bg-[#FCEEEA] p-3 rounded-md">
                      <div className="text-xs uppercase tracking-wider text-[#C05C47] font-semibold mb-2">
                        Pour confirmer, tapez <span className="font-mono bg-white px-1.5 py-0.5 rounded border">ACTIVER</span> :
                      </div>
                      <Input
                        autoFocus
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder="ACTIVER"
                        className="font-mono uppercase tracking-wide border-[#C05C47]"
                        data-testid="nayax-expert-mode-confirm-input"
                      />
                    </div>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setConfirmText("")}
              data-testid="nayax-expert-mode-cancel"
            >
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              disabled={saving || (!isEnabled && confirmText !== "ACTIVER")}
              className={isEnabled ? "bg-[#1F4E3D] hover:bg-[#173B2E]" : "bg-[#C05C47] hover:bg-[#A04A38]"}
              data-testid="nayax-expert-mode-confirm-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {isEnabled ? "Désactiver" : "Activer le mode expert"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
